import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import DressCard from "@/components/DressCard";
import HowItWorks from "@/components/HowItWorks";
import StoreFitting from "@/components/StoreFitting";
import InquiryForm from "@/components/InquiryForm";
import VipBanner from "@/components/VipBanner";
import HandcraftedManifesto from "@/components/HandcraftedManifesto";
import { api } from "@/lib/api";
import { buildWhatsappLink, genericInquiryMessage } from "@/lib/whatsapp";

export default function Home() {
  const [dresses, setDresses] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/dresses").then((r) => setDresses(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const featured = dresses.slice(0, 6);
  const waLink = buildWhatsappLink(settings?.whatsapp_number || "", genericInquiryMessage());

  return (
    <div data-testid="home-page" className="bg-cream">
      <Navbar />

      {/* Hero — Editorial Couture Luxury: Black + Gold */}
      <section className="relative min-h-[100svh] flex items-center justify-center overflow-hidden bg-black">
        {/* Soft gold radial glow — center */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 60% 50% at 50% 45%, rgba(212,175,55,0.18) 0%, rgba(0,0,0,0) 70%)",
          }}
        />
        {/* Secondary gold accent — top right */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 85% 15%, rgba(212,175,55,0.10) 0%, rgba(0,0,0,0) 50%)",
          }}
        />
        {/* Film grain noise */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.06] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/></svg>\")",
          }}
        />
        {/* Subtle logo watermark — background, very low opacity */}
        <img
          src="https://customer-assets.emergentagent.com/job_dress-rental-8/artifacts/addnkbh0_01282ab7-d3ab-4bd8-a457-edb52e8d5072.jpeg"
          alt=""
          aria-hidden="true"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[120vw] sm:w-[110vw] md:w-[90vw] lg:w-[75vw] xl:w-[65vw] h-auto pointer-events-none select-none"
          style={{
            mixBlendMode: "screen",
            opacity: 0.08,
            WebkitMaskImage:
              "radial-gradient(ellipse 60% 60% at 50% 50%, black 30%, transparent 80%)",
            maskImage:
              "radial-gradient(ellipse 60% 60% at 50% 50%, black 30%, transparent 80%)",
            filter: "blur(1px)",
          }}
        />

        {/* Foreground content */}
        <div className="relative w-full max-w-5xl mx-auto px-6 md:px-12 py-28 text-center text-cream flex flex-col items-center">
          {/* Small logo mark above text */}
          <img
            src="https://customer-assets.emergentagent.com/job_dress-rental-8/artifacts/addnkbh0_01282ab7-d3ab-4bd8-a457-edb52e8d5072.jpeg"
            alt="10/10 Kleiderverleih"
            data-testid="hero-logo"
            className="block mx-auto w-auto h-24 sm:h-28 md:h-32 object-contain animate-fade-up mb-2"
            style={{
              animationDelay: "60ms",
              mixBlendMode: "screen",
              WebkitMaskImage:
                "radial-gradient(ellipse 70% 80% at 50% 50%, black 55%, transparent 100%)",
              maskImage:
                "radial-gradient(ellipse 70% 80% at 50% 50%, black 55%, transparent 100%)",
              filter: "drop-shadow(0 0 24px rgba(212,175,55,0.45))",
            }}
          />

          {/* Decorative gold divider */}
          <div
            className="flex items-center gap-4 mt-2 mb-6 animate-fade-up"
            style={{ animationDelay: "120ms" }}
          >
            <span className="h-px w-12 sm:w-20 bg-gradient-to-r from-transparent to-gold" />
            <span className="text-[10px] sm:text-xs tracking-[0.5em] text-gold uppercase">
              Premium · Couture · Verleih
            </span>
            <span className="h-px w-12 sm:w-20 bg-gradient-to-l from-transparent to-gold" />
          </div>

          {/* Editorial headline — serif, italic accent */}
          <h1
            className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[1.02] tracking-tight text-cream animate-fade-up"
            style={{ animationDelay: "200ms" }}
          >
            Designerkleider.
            <br />
            <em className="text-gold not-italic font-light">Für deinen großen Moment.</em>
          </h1>

          <p
            className="mt-8 text-base sm:text-lg md:text-xl font-light text-cream/80 max-w-2xl animate-fade-up leading-relaxed"
            style={{ animationDelay: "300ms" }}
          >
            Mieten statt kaufen. Glänzen ohne Stress. <br className="hidden sm:block" />
            Eine kuratierte Auswahl handgefertigter Unikate – individuell anpassbar in Kassel.
          </p>

          {/* Domain */}
          <a
            href="https://10von10kleiderverleih.de"
            target="_blank"
            rel="noreferrer"
            data-testid="hero-domain"
            className="mt-8 inline-block text-xs sm:text-sm tracking-[0.4em] uppercase text-gold/90 hover:text-cream transition-colors duration-300 animate-fade-up border-b border-gold/40 pb-1"
            style={{ animationDelay: "380ms" }}
          >
            10von10kleiderverleih.de
          </a>

          {/* CTAs */}
          <div
            className="mt-10 flex flex-col sm:flex-row gap-3 justify-center animate-fade-up w-full sm:w-auto px-2"
            style={{ animationDelay: "460ms" }}
          >
            <Link
              to="/kleider"
              data-testid="hero-view-dresses"
              className="inline-flex items-center justify-center gap-3 bg-gold text-ink px-9 py-4 text-xs tracking-widest uppercase hover:bg-cream transition-colors duration-300"
            >
              Kollektion entdecken <ArrowRight size={16} />
            </Link>
            <a
              href={waLink}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-whatsapp"
              className="inline-flex items-center justify-center gap-3 border border-cream/30 text-cream hover:border-gold hover:text-gold px-9 py-4 text-xs tracking-widest uppercase transition-colors duration-300"
            >
              <MessageCircle size={16} /> WhatsApp Anfrage
            </a>
          </div>

          <div
            className="mt-12 inline-flex items-center gap-3 text-xs text-cream/60 animate-fade-up"
            style={{ animationDelay: "560ms" }}
          >
            <span className="w-1.5 h-1.5 bg-gold rounded-full inline-block" />
            100% Handarbeit · Jedes Kleid ein Unikat · Anpassung inklusive
          </div>
        </div>

        {/* Bottom gold hairline */}
        <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
      </section>

      {/* Premium VIP Banner */}
      <VipBanner />

      {/* Handcrafted manifesto */}
      <HandcraftedManifesto />

      {/* Brand intro strip */}
      <section className="py-20 md:py-28 bg-cream">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <div className="overline mb-5">Boutique Verleih</div>
          <p className="font-display text-3xl md:text-5xl leading-[1.15] text-ink">
            Eine kuratierte Auswahl an Designerkleidern – persönlich angepasst,
            sorgfältig gepflegt und bereit für deinen <em className="text-gold">großen Auftritt</em>.
          </p>
        </div>
      </section>

      {/* Featured catalog */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex items-end justify-between mb-12 md:mb-16">
            <div>
              <div className="overline mb-3"><span className="gold-line" />Aktuelle Kollektion</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight">Die Kleider</h2>
            </div>
            <Link to="/kleider" className="hidden md:inline-flex items-center gap-2 text-sm uppercase tracking-widest link-underline">
              Alle ansehen <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
            {featured.map((d, i) => (
              <DressCard key={d.id} dress={d} whatsappNumber={settings?.whatsapp_number} index={i} />
            ))}
          </div>

          {dresses.length === 0 && (
            <div data-testid="home-empty" className="py-20 md:py-28 text-center border border-dashed border-[#E5E0D8] bg-white">
              <div className="overline mb-4 text-gold">In Vorbereitung</div>
              <h3 className="font-display text-3xl md:text-4xl mb-3">Unsere Kollektion wird gerade vorbereitet.</h3>
              <p className="text-ink/65 max-w-md mx-auto">
                Bald findest du hier neue Kleider. Schreib uns gerne über WhatsApp – wir beraten dich auch persönlich.
              </p>
            </div>
          )}

          <div className="mt-14 text-center md:hidden">
            <Link to="/kleider" className="inline-flex items-center gap-2 bg-ink text-cream px-8 py-4 text-xs tracking-widest uppercase">
              Alle Kleider ansehen <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <HowItWorks />
      <StoreFitting />

      {/* Inquiry */}
      <section id="anfrage" data-testid="inquiry-section" className="py-24 md:py-32 bg-white border-y border-[#E5E0D8]">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-20">
            <div>
              <div className="overline mb-4"><span className="gold-line" />Direktkontakt</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight">Schreib uns – wir melden uns persönlich.</h2>
              <p className="mt-6 text-ink/70 max-w-md leading-relaxed">
                Du hast ein Lieblingskleid gefunden oder brauchst Beratung? Schick uns dein Wunschdatum –
                wir reservieren das Kleid und bestätigen den Termin für deine Anprobe.
              </p>
              <div className="mt-8 text-sm text-ink/60">
                Lieber direkt? <a href={waLink} target="_blank" rel="noreferrer" className="link-underline text-ink">WhatsApp öffnen</a>
              </div>
            </div>
            <InquiryForm />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
